import { urlEditProduct, urlProductList } from '@/urls';
import { productApi } from '@repo/api-client';
import { LoaderFunctionArgs, useLoaderData, useNavigate } from 'react-router';
import { Input } from '@repo/ui/components/form/input';
import { Button } from '@repo/ui/components/button';
import { useForm } from '@repo/ui/hooks/use-form';
import { useAsync } from '@repo/ui/hooks/use-async';
import { useCurrentClub } from '@/hooks/use-current-club';
import { Image } from '@repo/ui/components/image';
import { Select } from '@repo/ui/components/form/select';
import { File } from '@repo/ui/components/form/file';
import { toast } from 'sonner';
import { Checkbox } from '@repo/ui/components/form/checkbox';

function EditProduct() {
  const navigate = useNavigate();
  const club = useCurrentClub();
  const [product, categories] = useLoaderData<typeof route.loader>();

  const {
    ref: imageForm,
    values: imageValues,
    handleChangeValue,
  } = useForm({
    initialValues: {
      image: undefined,
      imagePreview: undefined,
    },
    onSubmit: async () => {
      const body = new FormData();
      if (imageValues.image) {
        body.append('image', imageValues.image);
      }
      await productApi.updateImage(club.id, product.id as string, body);
      toast.success('Logo actualizado correctamente');
    },
  });

  const { execute: onSubmit } = useAsync(async () => {
    await productApi.updateProduct(club.id, product.id as string, values);
    navigate(urlProductList(club.id), { viewTransition: true });
  });

  const { ref, values, errors } = useForm({
    initialValues: {
      sku: product.sku,
      name: product.name,
      price: product.price,
      categoryId: product.category?.id || '',
      showOnPOS: product.showOnPOS,
    },
    onSubmit,
  });

  const image = imageValues.imagePreview || product.imageUrl || '';

  return (
    <div className="flex gap-4 w-full items-center">
      <div className="flex justify-center gap-4 flex-1">
        <form ref={imageForm} className="flex flex-col gap-4">
          <File
            label="Imagen"
            name="imagePreview"
            errors={errors}
            required
            accept="image/*"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (!file) return;
              const previewUrl = URL.createObjectURL(file);
              handleChangeValue('imagePreview', previewUrl);
              handleChangeValue('image', file);
            }}
          >
            <Image src={image} alt={product.name} className="size-24" />
            <span className="text-sm font-medium text-primary underline">
              Click aqui para cambiar la imagen
            </span>
          </File>
          <Button type="submit">Guardar</Button>
        </form>
      </div>
      <form ref={ref} className="flex flex-col gap-4 flex-1">
        <Input label="SKU" type="text" name="sku" required errors={errors} />
        <Input label="Nombre" type="text" name="name" required errors={errors} />
        <Input label="Precio" type="number" name="price" required errors={errors} />
        <Checkbox
          id="showOnPOS"
          label="Mostrar en POS"
          name="showOnPOS"
          errors={errors}
          description="Se muestra o no en el catalogo del POS. Si no se muestra, aun asi se puede vender en el POS escaneando el codigo de barras."
        />
        <Select
          label="Categoría"
          name="categoryId"
          options={[
            { label: 'Sin categoría', value: '' },
            ...categories.results.map((category) => ({
              label: category.name,
              value: category.id,
            })),
          ]}
          required
          errors={errors}
        />
        <Button type="submit">Guardar</Button>
      </form>
    </div>
  );
}

export const route = {
  path: urlEditProduct(':id', ':productId'),
  element: <EditProduct />,
  loader: async ({ params }: LoaderFunctionArgs) => {
    const clubId = params.id as string;
    const productId = params.productId as string;
    const product = await productApi.getProductById(clubId, productId);
    const categories = await productApi.getCategories(clubId);
    const result = Promise.all([product, categories]);
    return result;
  },
  handle: {
    title: (data: [{ name: string }]) => `Editar producto ${data[0]?.name}`,
    backLink: (params: { id: string; productId: string }) => urlProductList(params.id),
  },
};
